import { derived, get, writable } from 'svelte/store';
import { defineAbility } from '@casl/ability';

import { getBootstrap, updateBootstrap } from '@client/app/bootstrap';

import { bannersStore } from '@client/components/banners';

import { LocalStorage } from '@client/utils/local-storage';
import { userMetadata } from '@client/utils/users';

let user = LocalStorage.get('user');

if (user === undefined) {
  user = {};
  LocalStorage.create('user', user);
} else {
  const { apitoken, token } = getBootstrap();

  if (token) {
    user.token = token;
  }

  if (user.token) {
    updateBootstrap('token', user.token);
  }

  if (apitoken) {
    user.apiToken = apitoken;
  }

  if (user.apiToken) {
    updateBootstrap('apitoken', user.apiToken);
  }
}

export const userStore = writable(user);

const STAFF_EMAIL_DOMAINS = ['opensense.com', 'sendergen.com'];

const generateUserRoles = ({ domainadmin, features, marketing, signatures, staff, settings, user }) => {
  const { context } = getBootstrap();
  const domainActions = staff || domainadmin ? ['manage'] : [];
  const manageActions = staff || signatures || marketing || domainadmin ? ['manage'] : [];
  const marketingActions = staff || marketing || domainadmin ? ['manage'] : [];
  const bizcardActions = (staff || signatures || domainadmin) && features.includes('enablebizcards') ? ['manage'] : [];
  const brandKitActions = (staff || signatures || domainadmin) && features.includes('enablebizcards') ? ['manage'] : []; // TODO?
  const signatureActions = staff || signatures || domainadmin ? ['manage'] : [];
  const userActions = staff || user ? ['manage'] : [];
  const staffActions = staff ? ['manage'] : [];
  // const billingActions = billing ? ['manage'] : [];

  return {
    domainadmin: domainActions,
    manage: manageActions,
    'manage:assets': manageActions,
    'manage:documents': manageActions,
    'manage:domains': manageActions,
    'manage:groups': manageActions,
    'manage:history': manageActions,
    'manage:images': manageActions,
    'manage:integrations': manageActions,
    'manage:routing': signatureActions,
    'manage:settings': domainActions,
    'manage:users': manageActions,
    'manage:videos': manageActions,

    marketing: marketingActions,
    'marketing:campaigns': marketingActions,
    'marketing:performance': marketingActions,
    'marketing:surveys': marketingActions,

    signatures: signatureActions,
    'signatures:studio': signatureActions,
    'signatures:pusher': signatureActions,

    bizcards: bizcardActions,
    'bizcards:studio': bizcardActions,
    'bizcards:routing': bizcardActions,

    brandkit: brandKitActions,
    'brandkit:studio': brandKitActions,
    'brandkit:routing': brandKitActions,

    staff: staffActions,

    user: userActions,
    'user:activity': userActions.length && (context?.source === 'sfdc' || settings?.enablesales) ? userActions : []
  };
};

export const ability = (config) => {
  const types = Object.keys(config);

  return defineAbility((can) => {
    types.forEach((type) => {
      if (type === 'roles') {
        const definitions = config[type];
        const roles = Object.keys(definitions);

        roles.forEach((role) => {
          can(definitions[role], role);
        });
      }

      if (type === 'features') {
        const features = config[type];

        features.forEach((feature) => {
          can(`feature:${feature}`);
        });
      }
    });
  });
};

const reduce = (config) =>
  Object.keys(config)
    .map((key) => config[key] && key)
    .filter(Boolean);

export const processUserConfig = async (config) => {
  const { clearBanners } = bannersStore;

  clearBanners();

  const features = reduce(config.domainFeatures || {});
  const featuresConfig = features.reduce((obj, key) => (obj[key] = config.domainFeatures[key]) && obj, {});
  const emailDomain = config.authEmail.split('@').pop();
  const isStaff = Boolean(config.roles?.staff) || STAFF_EMAIL_DOMAINS.includes(emailDomain);
  const isManaging = config.authDomain !== emailDomain;

  const metadata = userMetadata({
    domainname: config.authDomain,
    features,
    email: config.authEmail,
    params: config.userparams,
    ...config
  });

  const userConfig = {
    ability: {},
    apiToken: config.apitoken,
    cname: [config.cnameproto, config.cname],
    domain: config.authDomain,
    // Agent-row count; the eligible set is this plus the home domain, so >= 1 means more than one.
    domaincount: Number(config.domaincount) || 0,
    features,
    featuresConfig,
    isManaging,
    isStaff,
    currentUser: {
      ...metadata,
      email: config.authEmail,
      username: config.authEmail.split('@').shift()
    },
    roles: generateUserRoles({
      ...config.roles,
      features,
      settings: config.usersettings,
      staff: isStaff
    }),
    hasSalesforce: config.sfdcEnabled,
    settings: config.usersettings,
    token: config.jwt
  };

  if (userConfig.token) {
    updateBootstrap('token', userConfig.token);
  }

  userConfig.ability = ability({
    features: userConfig.features,
    roles: userConfig.roles
  });

  LocalStorage.update('user', userConfig);

  return userConfig;
};

export const hasCapability = (action, subject) => {
  if (!subject) {
    return get(userStore)?.ability?.can(action);
  }

  return get(userStore)?.ability?.can(action, subject);
};
